import React from "react";
import { ClientApartment, ReservationExtra, AptPackage, PromoValidateResult } from "../../../utils/api/apartmentRentalsAPI";
import { MealSelection } from "../../../utils/api/mealsAPI";
import { CalDate, fmtAmount, fmtDate, StayResult } from "./bookingPricing";
import PromoBlock from "./PromoBlock";

interface Props {
  apt: ClientApartment;
  checkIn: CalDate;
  checkOut: CalDate;
  nights: number;
  adults: number;
  children: number;
  stayInfo: StayResult;
  matchedPackage: AptPackage | null;
  packageNightsTotal: number | null;
  packageChildCostRows: Array<{ label: string; amount: number }>;
  effectiveNightsTotal: number;
  selectedExtras: ReservationExtra[];
  extrasTotal: number;
  mealSelections: MealSelection[];
  mealsTotal: number;
  promoDiscount: number;
  finalAmount: number;
  submitError: string;
  translateLabel: (label: string) => string;
  lang: string;
  t: (key: any) => any;
  showPromoInline?: boolean;
  promoInput?: string;
  setPromoInput?: (v: string) => void;
  promoResult?: PromoValidateResult | null;
  promoLoading?: boolean;
  onValidatePromo?: () => void;
  onClearPromo?: () => void;
}

const StepSummary: React.FC<Props> = ({
  apt, checkIn, checkOut, nights, adults, children, stayInfo,
  matchedPackage, packageNightsTotal, packageChildCostRows, effectiveNightsTotal,
  selectedExtras, extrasTotal, mealSelections, mealsTotal, promoDiscount, finalAmount,
  submitError, translateLabel, lang, t,
  showPromoInline, promoInput, setPromoInput, promoResult, promoLoading, onValidatePromo, onClearPromo,
}) => {
  const totalGuests = adults + children;

  return (
    <div className="pab-step">
      <h2 className="pab-step__title">{lang === "en" ? "Review & confirm" : "Pregled rezervacije"}</h2>
      <div className="pab-step__subtitle">
        {lang === "en" ? "Check everything looks right before you confirm" : "Provjerite detalje prije potvrde"}
      </div>

      <div className="pab-summary-card">
        <div className="pab-summary-card__apt">
          <div className="pab-summary-card__apt-name">{apt.name}</div>
          <div className="pab-summary-card__apt-loc">📍 {apt.location}</div>
        </div>

        <div className="pab-summary-card__block">
          <div className="pab-summary-card__block-label">{lang === "en" ? "Dates" : "Datum"}</div>
          <div className="pab-summary-card__block-value">
            {fmtDate(checkIn.year, checkIn.month, checkIn.day)} – {fmtDate(checkOut.year, checkOut.month, checkOut.day)}
            <span className="pab-summary-card__nights"> · {nights} {nights === 1 ? (lang === "en" ? "night" : "noć") : (lang === "en" ? "nights" : "noći")}</span>
          </div>
        </div>

        <div className="pab-summary-card__block">
          <div className="pab-summary-card__block-label">{lang === "en" ? "Guests" : "Gosti"}</div>
          <div className="pab-summary-card__block-value">
            {totalGuests} {lang === "en" ? (totalGuests === 1 ? "guest" : "guests") : (totalGuests === 1 ? "gost" : "gosta")}
            {children > 0 && <span className="pab-summary-card__nights"> · {adults} {lang === "en" ? "adults" : "odraslih"}, {children} {lang === "en" ? "children" : "djece"}</span>}
          </div>
        </div>

        <div className="pab-summary-card__divider" />

        <div className="pab-summary-card__rows">
          {packageNightsTotal != null && matchedPackage ? (
            <>
              <div className="pab-summary-row">
                <span>
                  {nights} {nights === 1 ? "noć" : "noći"} × {fmtAmount((packageNightsTotal - packageChildCostRows.reduce((s, r) => s + r.amount, 0)) / nights, apt.currency)}
                  <span className="pab-summary-row__tag">🎁 {matchedPackage.name}</span>
                </span>
                <span className="pab-summary-row__val">{fmtAmount(packageNightsTotal - packageChildCostRows.reduce((s, r) => s + r.amount, 0), apt.currency)}</span>
              </div>
              {packageChildCostRows.map((row, i) => (
                <div className="pab-summary-row" key={`child-${i}`}>
                  <span>👶 {row.label}</span>
                  <span className="pab-summary-row__val">{fmtAmount(row.amount, apt.currency)}</span>
                </div>
              ))}
            </>
          ) : stayInfo.groups.map((g, i) => (
            <div className="pab-summary-row" key={i}>
              <span>
                {g.nights} {g.nights === 1 ? "noć" : "noći"} × {fmtAmount(g.pricePerNight, apt.currency)}
                {g.label !== "__standardPrice__" && (
                  <span className="pab-summary-row__tag">{translateLabel(g.label)}</span>
                )}
              </span>
              <span className="pab-summary-row__val">{fmtAmount(g.total, apt.currency)}</span>
            </div>
          ))}

          {extrasTotal > 0 && selectedExtras.map((e) => (
            <div className="pab-summary-row" key={e.extraId}>
              <span>{e.name} × {e.quantity}</span>
              <span className="pab-summary-row__val">{fmtAmount(e.total, apt.currency)}</span>
            </div>
          ))}

          {mealSelections.length > 0 && (
            <div className="pab-summary-meals">
              <div className="pab-summary-row pab-summary-row--head">
                <span>🍽️ {lang === "en" ? "Meals" : "Obroci"}</span>
                <span className="pab-summary-row__val">{mealsTotal > 0 ? fmtAmount(mealsTotal, apt.currency) : (lang === "en" ? "included" : "uključeno")}</span>
              </div>
              {mealSelections.map((ms, i) => (
                <div className="pab-summary-row pab-summary-row--sub" key={i}>
                  <span>
                    {fmtDate(...(ms.date.split("-").map(Number) as [number, number, number]))} · {ms.mealName}{ms.itemName ? ` — ${ms.itemName}` : ""} · {ms.persons} {lang === "en" ? (ms.persons === 1 ? "guest" : "guests") : (ms.persons === 1 ? "gost" : "gosta")}
                  </span>
                  <span className="pab-summary-row__val">
                    {ms.isIncluded ? (lang === "en" ? "included" : "uklj.") : fmtAmount(ms.price, apt.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {promoDiscount > 0 && (
            <div className="pab-summary-row pab-summary-row--ok">
              <span>🎉 {lang === "en" ? "Promo discount" : "Promo popust"}</span>
              <span className="pab-summary-row__val">−{fmtAmount(promoDiscount, apt.currency)}</span>
            </div>
          )}
        </div>

        <div className="pab-summary-card__divider" />

        <div className="pab-summary-card__total">
          <span>{t("total")}</span>
          <span>{fmtAmount(finalAmount, apt.currency)}</span>
        </div>
      </div>

      {showPromoInline && setPromoInput && onValidatePromo && onClearPromo && (
        <>
          <div className="pab-block-title">{lang === "en" ? "Promo code" : "Promo kod"}</div>
          <PromoBlock
            promoInput={promoInput || ""}
            setPromoInput={setPromoInput}
            promoResult={promoResult ?? null}
            promoLoading={!!promoLoading}
            onValidate={onValidatePromo}
            onClear={onClearPromo}
            currency={apt.currency}
            placeholder={t("promoCode")}
            applyLabel={t("apply")}
          />
        </>
      )}

      {submitError && (
        <div className="pab-notice pab-notice--error">
          <span>⚠</span>
          <span>{submitError}</span>
        </div>
      )}

      <div className="pab-summary-fineprint">
        {lang === "en" ? "Your reservation will await confirmation from the host." : "Rezervacija čeka potvrdu od strane vlasnika."}
      </div>
    </div>
  );
};

export default StepSummary;
